import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  Box,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { useParams } from "react-router";
import { useDispatch } from "react-redux";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import A_SimpleDialog from "../../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import useStyles from "./AddCompletionStatementForm.styles";
import { httpPost } from "../../../../../../Utils/apis/apis";
import * as toastActions from "../../../../../../store/actions/toastActions";
import { toastSeverity } from "../../../../../../Utils/config/toastConfig";

const INITIAL_STATE = {
  disbursements: "",
  firstCertificateFee: "",
  fee1: "",
  fee1Amount: "",
  fee2: "",
  fee2Amount: "",
  fee3: "",
  fee3Amount: "",
  fee4: "",
  fee4Amount: "",
  fee5: "",
  fee5Amount: "",
};

const AddCompletionStatementForm = (props) => {
  const { open, onClose, title, afterClose } = props;
  const { crmId } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visibleFeeCount, setVisibleFeeCount] = useState(1);

  useEffect(() => {
    if (open && !loading) {
      fetchPrefilledData();
    }
  }, [open]);

  const fetchPrefilledData = async () => {
    try {
      setLoading(true);
      const payload = [
        {
          id: "completionStatement",
          spName: "DF.usp_GetPrefilledCompletionStatementData",
          parameters: {
            LoanId: crmId,
          },
        },
      ];
      const response = await httpPost(
        "/SQLQuery/getDataFromMultipleSPs?chartSPs=false",
        payload
      );

      if (
        response &&
        response.completionStatement &&
        response.completionStatement.length > 0
      ) {
        const data = response.completionStatement;
        const newFormData = { ...INITIAL_STATE };

        // Map special fields
        const disbursementsItem = data.find(
          (item) => item.Category === "Disbursements"
        );
        const certFeeItem = data.find(
          (item) => item.Category === "1st Certificate Fee"
        );

        if (disbursementsItem) {
          newFormData.disbursements = disbursementsItem.Value || "";
        }
        if (certFeeItem) {
          newFormData.firstCertificateFee = certFeeItem.Value || "";
        }

        // Map remaining items to fees (in order)
        const feeItems = data.filter(
          (item) =>
            item.Category !== "Disbursements" &&
            item.Category !== "1st Certificate Fee"
        );

        feeItems.forEach((item, index) => {
          if (index < 5) {
            const feeNum = index + 1;
            newFormData[`fee${feeNum}`] = item.Category || "";
            newFormData[`fee${feeNum}Amount`] = item.Value || "";
          }
        });

        setFormData(newFormData);
        setVisibleFeeCount(Math.min(feeItems.length + 1, 5));
      }
    } catch (error) {
      console.error("Error fetching prefilled data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    clearError(name);
  };

  const clearError = (fieldName) => {
    setErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};

    if (formData.disbursements && parseFloat(formData.disbursements) < 0) {
      nextErrors.disbursements = "Amount cannot be negative";
    }

    if (
      formData.firstCertificateFee &&
      parseFloat(formData.firstCertificateFee) < 0
    ) {
      nextErrors.firstCertificateFee = "Amount cannot be negative";
    }

    // Validate visible fees
    let lastFilledFeeIndex = -1;
    for (let feeNum = 1; feeNum <= visibleFeeCount; feeNum++) {
      const feeDescKey = `fee${feeNum}`;
      const feeAmountKey = `fee${feeNum}Amount`;
      const hasDesc = formData[feeDescKey] && formData[feeDescKey].trim();
      const hasAmount = formData[feeAmountKey] && formData[feeAmountKey] !== "";

      // If one exists, both must exist
      if (hasDesc && !hasAmount) {
        nextErrors[feeAmountKey] =
          "Amount is required when description is provided";
      }
      if (hasAmount && !hasDesc) {
        nextErrors[feeDescKey] =
          "Description is required when amount is provided";
      }

      // Check for negative amounts
      if (hasAmount && parseFloat(formData[feeAmountKey]) < 0) {
        nextErrors[feeAmountKey] = "Amount cannot be negative";
      }

      // Track last filled fee
      if (hasDesc || hasAmount) {
        lastFilledFeeIndex = feeNum;
      }
    }

    // Check for gaps - if fee N is filled, all previous visible fees must be filled
    for (let i = 1; i <= lastFilledFeeIndex; i++) {
      const feeDescKey = `fee${i}`;
      const feeAmountKey = `fee${i}Amount`;
      const hasDesc = formData[feeDescKey] && formData[feeDescKey].trim();
      const hasAmount = formData[feeAmountKey] && formData[feeAmountKey] !== "";

      if (!hasDesc || !hasAmount) {
        nextErrors[
          feeDescKey
        ] = `Fee ${i} must be complete (can't skip to Fee ${lastFilledFeeIndex})`;
        nextErrors[
          feeAmountKey
        ] = `Fee ${i} must be complete (can't skip to Fee ${lastFilledFeeIndex})`;
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const completionData = {
        LoanId: crmId,
        Disbursements: parseFloat(formData.disbursements) || 0,
        FirstCertificateFee: parseFloat(formData.firstCertificateFee) || 0,
        Fee1: formData.fee1 || null,
        Fee1Amount: parseFloat(formData.fee1Amount) || 0,
        Fee2: formData.fee2 || null,
        Fee2Amount: parseFloat(formData.fee2Amount) || 0,
        Fee3: formData.fee3 || null,
        Fee3Amount: parseFloat(formData.fee3Amount) || 0,
        Fee4: formData.fee4 || null,
        Fee4Amount: parseFloat(formData.fee4Amount) || 0,
        Fee5: formData.fee5 || null,
        Fee5Amount: parseFloat(formData.fee5Amount) || 0,
      };

      const payload = [
        {
          id: "saveCompletionStatement",
          spName: "DF.usp_AddCompletionStatement",
          parameters: {
            JSON: JSON.stringify(completionData),
          },
        },
      ];
      const response = await httpPost(
        "/SQLQuery/getDataFromMultipleSPs?chartSPs=false",
        payload
      );
      if (response instanceof Error) {
        throw response;
      }

      if (response && !response.Message && !response.error) {
        dispatch(
          toastActions.setToast({
            type: toastSeverity.SUCCESS,
            severity: toastSeverity.SUCCESS,
            message: "Completion statement saved successfully",
            duration: 3000,
          })
        );
        resetForm();
        onClose();
        if (afterClose) {
          setTimeout(() => {
            const pathSegments = window.location.pathname
              .split("/")
              .filter(Boolean);
            const pageUrl =
              pathSegments.length > 0
                ? pathSegments[0]
                : window.location.pathname;
            afterClose({
              pageUrl: pageUrl,
              crmId: crmId,
            });
          }, 300);
        }
        return;
      }

      const errorMsg =
        response?.Message ||
        response?.error ||
        "Failed to save completion statement. Please try again.";
      throw new Error(errorMsg);
    } catch (error) {
      console.error("Error saving completion statement:", error);
      console.error("Error response data:", error.response?.data);
      const errorMessage =
        error.response?.data?.Message ||
        error.message ||
        "Failed to save completion statement. Please try again.";

      setErrors((prev) => ({
        ...prev,
        submit: errorMessage,
      }));

      dispatch(
        toastActions.setToast({
          type: toastSeverity.ERROR,
          severity: toastSeverity.ERROR,
          message: errorMessage,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setFormData(INITIAL_STATE);
    setErrors({});
    setVisibleFeeCount(1);
  };

  const isFeeComplete = (feeNum) => {
    const desc = formData[`fee${feeNum}`]?.trim();
    const amount = formData[`fee${feeNum}Amount`];
    return desc && amount !== "";
  };

  const handleAddFee = () => {
    if (visibleFeeCount < 5) {
      const newFeeNum = visibleFeeCount + 1;
      // Clear data for the new fee row
      setFormData((prev) => ({
        ...prev,
        [`fee${newFeeNum}`]: "",
        [`fee${newFeeNum}Amount`]: "",
      }));
      clearError(`fee${newFeeNum}`);
      clearError(`fee${newFeeNum}Amount`);
      setVisibleFeeCount(newFeeNum);
    }
  };

  const handleDeleteFee = (feeNum) => {
    if (visibleFeeCount === 1) return;

    setFormData((prev) => {
      const updated = { ...prev };
      // Shift all fees after the deleted one up
      for (let i = feeNum; i < 5; i++) {
        updated[`fee${i}`] = updated[`fee${i + 1}`] || "";
        updated[`fee${i}Amount`] = updated[`fee${i + 1}Amount`] || "";
      }
      // Always clear the last fee slot
      updated.fee5 = "";
      updated.fee5Amount = "";
      return updated;
    });

    // Clear errors for all fees
    for (let i = 1; i <= 5; i++) {
      clearError(`fee${i}`);
      clearError(`fee${i}Amount`);
    }

    setVisibleFeeCount((prev) => prev - 1);
  };

  const renderFeeSection = (feeNum) => (
    <Box className={classes.feeSection} key={`fee-${feeNum}`}>
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <Typography className={classes.feeSectionTitle}>Fee {feeNum}</Typography>
        {visibleFeeCount > 1 && (
          <IconButton
            size="small"
            onClick={() => handleDeleteFee(feeNum)}
            style={{ color: "var(--color-error)" }}
            title="Delete this fee"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Grid container spacing={1} className={classes.gridSpacing}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={`Fee ${feeNum} Description`}
            name={`fee${feeNum}`}
            value={formData[`fee${feeNum}`]}
            onChange={handleChange}
            variant="outlined"
            size="small"
            className={classes.formField}
            placeholder="e.g., Processing Fee"
            error={Boolean(errors[`fee${feeNum}`])}
            helperText={errors[`fee${feeNum}`]}
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={`Fee ${feeNum} Amount`}
            name={`fee${feeNum}Amount`}
            type="number"
            value={formData[`fee${feeNum}Amount`]}
            onChange={handleChange}
            variant="outlined"
            size="small"
            className={`${classes.formField} ${classes.amountInput}`}
            inputProps={{ step: "1", min: "0" }}
            error={Boolean(errors[`fee${feeNum}Amount`])}
            helperText={errors[`fee${feeNum}Amount`]}
            disabled={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const formContent = (
    <Box className={classes.formContainer}>
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          <Typography className={classes.sectionLabel}>
            Base Information
          </Typography>
          <Box className={classes.sectionCard}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Disbursements"
                  name="disbursements"
                  type="number"
                  value={formData.disbursements}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                  className={`${classes.formField} ${classes.amountInput}`}
                  inputProps={{ step: "1", min: "0" }}
                  placeholder="Enter disbursement amount"
                  error={Boolean(errors.disbursements)}
                  helperText={errors.disbursements}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="1st Certificate Fee"
                  name="firstCertificateFee"
                  type="number"
                  value={formData.firstCertificateFee}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                  className={`${classes.formField} ${classes.amountInput}`}
                  inputProps={{ step: "1", min: "0" }}
                  placeholder="Enter certificate fee amount"
                  error={Boolean(errors.firstCertificateFee)}
                  helperText={errors.firstCertificateFee}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Box>

          <Typography className={classes.sectionLabel}>
            Additional Fees
          </Typography>
          <Box className={classes.sectionCard}>
            {Array.from({ length: visibleFeeCount }, (_, i) => i + 1).map(
              (feeNum) => renderFeeSection(feeNum)
            )}
            {visibleFeeCount < 5 && isFeeComplete(visibleFeeCount) && (
              <Box
                onClick={handleAddFee}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "12px",
                  cursor: "pointer",
                }}
              >
                <IconButton
                  size="small"
                  style={{ color: "var(--color-primary-text)" }}
                  title="Add another fee"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <Typography
                  style={{
                    fontSize: "0.85rem",
                    marginLeft: "4px",
                    color: "var(--color-text-secondary)",
                    cursor: "pointer",
                  }}
                >
                  Add Fee
                </Typography>
              </Box>
            )}
          </Box>

          {errors.submit && (
            <Typography className={classes.errorText}>
              {errors.submit}
            </Typography>
          )}
        </>
      )}
    </Box>
  );

  const dialogActions = (
    <Box className={classes.dialogFooter} style={{ borderTop: "none" }}>
      <A_Button
        label="Cancel"
        onClick={handleCancel}
        premiumVariant="secondary"
        className={classes.cancelBtn}
        disabled={saving}
        size="small"
      />
      <A_Button
        label={saving ? "Saving..." : "Save"}
        onClick={handleSave}
        premiumVariant="primary"
        className={classes.submitBtn}
        disabled={saving}
        size="small"
      />
    </Box>
  );

  return (
    <A_SimpleDialog
      open={open}
      onClose={handleCancel}
      title={title || "Add Completion Statement"}
      dialogContent={formContent}
      dialogActions={dialogActions}
      fullWidth
      maxWidth="md"
      showclosebutton="true"
      disableBackdropClick={true}
      liquidGlass={false}
    />
  );
};

AddCompletionStatementForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  afterClose: PropTypes.func,
};

AddCompletionStatementForm.defaultProps = {
  title: "Add Completion Statement",
  afterClose: null,
};

export default AddCompletionStatementForm;
